import React from "react";

import { useGetFootprintingDairyFootprint, useTranslation } from "utils";

import { FormFlowOverlay, FormPage as FormFlowStep } from "components";

import { FeedAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedAdditiveForm";
import { FeedForageLossForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedForageLossForm";
import { FeedInformationForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedInformationForm";
import { FeedPastureForm } from "pages/app/footprinting/components/formFlows/dairyFlow/FeedPastureForm";
import { CommercialClassesForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdCommercialClasses";
import { HerdConditionForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdConditionForm";
import { HerdDetailsForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdDetailsForm";
import { HerdOffspringForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdOffspringForm";
import { HerdSizeForm } from "pages/app/footprinting/components/formFlows/dairyFlow/HerdSizeForm";
import { ManureAdditiveForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureAdditiveForm";
import { ManureStorageForm } from "pages/app/footprinting/components/formFlows/dairyFlow/ManureStorageForm";
import { MilkYieldForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MilkYieldForm";
import { MoorlandForm } from "pages/app/footprinting/components/formFlows/dairyFlow/MoorlandForm";

interface DairyFlowProps {
  showFormFlow: boolean;
  onExit: () => void;
  farmId: string;
  year: number;
  readOnly?: boolean;
  initialStep?: number;
  editMode?: boolean;
}

const dairyFormCategories = {
  animals: "footprintingLandingPage.assessmentCategories.dairy.animalsBreed",
  feed: "footprintingLandingPage.assessmentCategories.dairy.feed",
  manure: "footprintingLandingPage.assessmentCategories.dairy.manure",
};

export const DairyFormFlow = ({ onExit, showFormFlow, farmId, year, readOnly, initialStep, editMode }: DairyFlowProps) => {
  const { t } = useTranslation("footprinting");
  const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year);
  const [currentStep, setCurrentStep] = React.useState<number | undefined>(initialStep ?? 0);

  const dairyFlowSteps: FormFlowStep[] = [
    {
      title: t("navigation.dairy.milkYield"),
      body: () => <MilkYieldForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.herdSize"),
      category: t(dairyFormCategories.animals),
      body: () => <HerdSizeForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.herdDetails"),
      category: t(dairyFormCategories.animals),
      body: () => <HerdDetailsForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.herdCondition"),
      category: t(dairyFormCategories.animals),
      body: () => <HerdConditionForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.herdOffspring"),
      category: t(dairyFormCategories.animals),
      body: () => <HerdOffspringForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.herdCommercialClasses"),
      category: t(dairyFormCategories.animals),
      body: () => (
        <CommercialClassesForm
          readOnly={readOnly}
          primaryDataId={dairyProgressQuery.data?.footprint?.id}
          farmId={farmId}
          year={year}
          onNavigateToStep={setCurrentStep}
        />
      ),
    },
    {
      title: t("navigation.dairy.feedPasture"),
      category: t(dairyFormCategories.feed),
      body: () => <FeedPastureForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.feedInformation"),
      category: t(dairyFormCategories.feed),
      body: () => <FeedInformationForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.additives"),
      category: t(dairyFormCategories.feed),
      body: () => <FeedAdditiveForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.feedForageLoss"),
      category: t(dairyFormCategories.feed),
      body: () => <FeedForageLossForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.manureStorage"),
      category: t(dairyFormCategories.manure),
      body: () => <ManureStorageForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.manureAdditive"),
      category: t(dairyFormCategories.manure),
      body: () => <ManureAdditiveForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.dairy.moorland"),
      body: () => <MoorlandForm readOnly={readOnly} primaryDataId={dairyProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
  ];

  return (
    <FormFlowOverlay
      onFormClose={onExit}
      showModal={showFormFlow}
      formPages={dairyFlowSteps}
      initialStep={currentStep}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      completionPercent={dairyProgressQuery.data?.completionPct ?? 0}
      getPageStatus={(pageNumber) => {
        if (dairyProgressQuery.data?.pagesCompleted.includes(pageNumber)) return "complete";
        if (dairyProgressQuery.data?.pagesIncomplete.includes(pageNumber)) return "incomplete";
        return null;
      }}
      // {/* TODO: Use translated crop names based on HF work */}
      navBarTitle={t("navigation.dairy.navBarTitle", { year })}
      dataTestId="crop-form-flow-overlay"
      editMode={editMode}
    />
  );
};
